import type { Tag } from "@repo/types";
import type { Row, Table } from "@tanstack/react-table";
import { useState } from "react";
import type { TagSource, TagWSource } from "./tags";
import { Chip } from "@repo/ui/components/Base/Chip";

const maxTagColumnDisplayChars = 200; // max chars of combined tag labels before more tags are collapsed

const tagSourceClasses: Record<TagSource, string> = {
  meeting: "", // /*@tw*/ "bg-primary hover:bg-primary/90",
  agenda: "", // /*@tw*/ "bg-primary hover:bg-primary/90",
  agendaModule: "", // /*@tw*/ "bg-tertiary hover:bg-tertiary/90",
  agendaItem: "", // /*@tw*/ "bg-tan hover:bg-tan/90",
  document: "", // /*@tw*/ "bg-secondary hover:bg-secondary/90",
};

export const TagList = <T,>({
  row,
  table,
  tagsKey = "tags" as keyof T,
}: {
  row: Row<T>;
  table: Table<T>;
  tagsKey?: keyof T;
}) => {
  const doc = row.original;

  const [expanded, setExpanded] = useState(false);

  const tags = ((doc[tagsKey] ?? []) as (Tag | string)[]).map((tag) =>
    typeof tag === "string" ? { text: tag, id: "" } : tag
  );
  const limitedTags: (Tag | "more")[] = [];

  if (expanded) {
    limitedTags.push(...tags, "more");
  } else {
    let chars = 0;
    for (const tag of tags) {
      chars += tag.text.length + 2; // +2 to account for the tag padding
      if (chars > maxTagColumnDisplayChars) {
        limitedTags.push("more");
        break;
      }
      limitedTags.push(tag);
    }
  }

  return (
    <div className="flex flex-row flex-wrap">
      {limitedTags.map((tag, index) =>
        tag === "more" ? (
          <Chip
            key="more"
            className="bg-gray-400 hover:bg-gray-300"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <>&laquo; Less</> : <>More...</>}
          </Chip>
        ) : (
          <Chip
            key={index}
            className={
              (tag as TagWSource).source
                ? tagSourceClasses[(tag as TagWSource).source]
                : ""
            }
            onClick={() =>
              table.getColumn(tagsKey as string)?.setFilterValue(tag.text)
            }
          >
            {tag.text}
          </Chip>
        )
      )}
    </div>
  );
};
